import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { listFetches } from "../../api/client";
import type { Fetch } from "../../api/models";
import { StatusBadge } from "../../components/Badge";
import { usePoll } from "../../hooks/usePoll";
import { errorMessage, formatDate } from "../../lib/format";

const active = (f: Fetch) => f.status === "RUNNING" || f.status === "QUEUED";

export function FetchesPage() {
  const [fetches, setFetches] = useState<Fetch[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(() => {
    listFetches()
      .then((list) => {
        setFetches(list);
        setSelectedId((cur) => cur ?? list[0]?.id ?? null);
      })
      .catch((e) => setError(errorMessage(e)));
  }, []);
  usePoll(load, 3000, fetches === null || fetches.some(active));

  const selected = fetches?.find((f) => f.id === selectedId) ?? null;

  return (
    <>
      <h1>Fetches</h1>
      {error && <p className="error">{error}</p>}
      {!fetches && !error && <p className="muted">Loading…</p>}
      {fetches && (
        <div className="two-col">
          <div className="table-wrap">
            <table className="compact">
              <thead>
                <tr>
                  <th>Fetch</th>
                  <th>Status</th>
                  <th>Params</th>
                  <th className="num">Added</th>
                  <th className="num">Pages</th>
                  <th>Run</th>
                  <th>By</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {fetches.map((f) => (
                  <tr
                    key={f.id}
                    className={`clickable${f.id === selectedId ? " selected" : ""}`}
                    onClick={() => setSelectedId(f.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setSelectedId(f.id);
                    }}
                  >
                    <td>#{f.id}</td>
                    <td>
                      <StatusBadge status={f.status} />
                    </td>
                    <td className="mono muted">
                      {f.params.count} · p{f.params.minPage}–{f.params.maxPage} · {f.params.perPage}/page
                      {f.params.seed != null ? ` · seed ${f.params.seed}` : ""}
                    </td>
                    <td className="num">{f.added}</td>
                    <td className="num">{f.pagesSeen}</td>
                    <td>
                      {f.runId != null ? (
                        <Link to={`/runs/${f.runId}`} onClick={(e) => e.stopPropagation()}>
                          #{f.runId}
                        </Link>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="muted">{f.createdBy}</td>
                    <td className="nowrap">{formatDate(f.createdAt)}</td>
                  </tr>
                ))}
                {fetches.length === 0 && (
                  <tr>
                    <td colSpan={8} className="muted">
                      No fetches yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div>
            {selected ? (
              <>
                <h3>
                  Fetch #{selected.id} log
                  {selected.error && <span className="error"> · {selected.error}</span>}
                </h3>
                <pre className="log">{selected.log || "(empty)"}</pre>
              </>
            ) : (
              <p className="muted">Select a fetch to see its log.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
